import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Alert, FormGroup, Input, Label } from 'reactstrap';
import Table from '../../../common/Table';
import { generateBusCurrentStatusColumns } from '../../../../config/columns';
import { DEFAULT_PAGE_SIZE } from '../../../../config/constants';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import {
  elapsedTimeFormat,
  tzDiff,
  tzNormalizeDate,
} from '../../../../utils/date';
import BusFleetCurrentStatusFilterForm from './BusFleetCurrentStatusFilterForm';
import BusCurrentStatusEvidence from './BusCurrentStatusEvidence';
import FleetStatusCounterCards from './FleetStatusCounterCards';

const AUTO_REFRESH_INTERVAL_MS = 30 * 1000;

const STALE_DATA_THRESHOLD_MINUTES = 5;

const DATA_AGE_TICK_INTERVAL_MS = 30 * 1000;

const BusFleetCurrentStatusPanel = ({
  buses,
  counters,
  evaluatedAt,
  countersConsistent,
  loading,
  initialFilters,
  onSearch,
  onRefresh,
}) => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [, tickDataAge] = useState(0);

  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const intervalId = setInterval(() => {
      if (loadingRef.current) return;

      onRefresh();
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [autoRefresh, onRefresh]);

  useEffect(() => {
    if (autoRefresh) return undefined;

    const intervalId = setInterval(
      () => tickDataAge((tick) => tick + 1),
      DATA_AGE_TICK_INTERVAL_MS,
    );

    return () => clearInterval(intervalId);
  }, [autoRefresh]);

  const handleSearch = ({
    baseLocation,
    serviceType,
    showNorthChico,
    showMine,
  }) => {
    const newFilters = {
      showNorthChico: showNorthChico || false,
      showMine: showMine || false,
    };

    if (baseLocation) {
      newFilters.baseLocationId = baseLocation.value;
    }

    if (serviceType && serviceType.length) {
      newFilters.serviceTypeIds = serviceType.map(({ value }) => value);
    }

    onSearch(newFilters);
  };

  const columns = useMemo(
    () =>
      generateBusCurrentStatusColumns({
        evaluatedAt,
        onRefresh,
      }),
    [evaluatedAt, onRefresh],
  );

  const renderEvidence = useCallback(
    (row) => <BusCurrentStatusEvidence bus={row.original} />,
    [],
  );

  const dataAgeMinutes = evaluatedAt
    ? tzDiff({ startDate: evaluatedAt, endDate: undefined, unit: 'minutes' })
    : null;

  const isDataStale =
    dataAgeMinutes !== null && dataAgeMinutes >= STALE_DATA_THRESHOLD_MINUTES;

  return (
    <>
      <FleetStatusCounterCards counters={counters} />

      {countersConsistent === false ? (
        <Alert color="warning">
          Los contadores no cuadran con la flota total, revisar con soporte. La
          lista de buses se muestra igual para poder ubicar el bus sin
          clasificar.
        </Alert>
      ) : null}

      <BusFleetCurrentStatusFilterForm
        onSubmit={handleSearch}
        initialValues={initialFilters}
        loading={loading}
      />

      <div className="mt-2 d-flex flex-wrap align-items-center justify-content-end">
        <FormGroup check className="mb-0">
          <Label check className="small text-muted">
            <Input
              type="checkbox"
              checked={autoRefresh}
              onChange={({ target }) => setAutoRefresh(target.checked)}
            />{' '}
            Actualización automática
          </Label>
        </FormGroup>
      </div>

      <div className="text-muted small text-right mt-2">
        {evaluatedAt ? (
          <>
            {`${buses.length} buses · actualizado al ${tzNormalizeDate({
              date: evaluatedAt,
              format: DATE_TIME_FORMAT,
            })}`}
            {!autoRefresh && (
              <span className={isDataStale ? 'text-warning' : undefined}>
                {` · actualizado hace ${elapsedTimeFormat(dataAgeMinutes)}`}
              </span>
            )}
          </>
        ) : (
          'Sin datos'
        )}
      </div>

      <Table
        columns={columns}
        data={buses}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        manual={false}
        loading={loading}
        rowComponent={renderEvidence}
        filterable
      />
    </>
  );
};

BusFleetCurrentStatusPanel.propTypes = {
  buses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  counters: PropTypes.shape({}),
  evaluatedAt: PropTypes.string,
  countersConsistent: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  initialFilters: PropTypes.shape({}).isRequired,
  onSearch: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

BusFleetCurrentStatusPanel.defaultProps = {
  counters: null,
  evaluatedAt: null,
  countersConsistent: null,
};

const mapStateToProps = ({ BusUnit }) => {
  const { counters, buses, evaluatedAt, countersConsistent } =
    BusUnit.RegisteredBusFleet.getIn(['currentStatus', 'content']).toJS();

  return {
    buses: buses || [],
    counters: counters || null,
    evaluatedAt: evaluatedAt || null,
    countersConsistent:
      countersConsistent === undefined ? null : countersConsistent,
    loading: BusUnit.RegisteredBusFleet.getIn(['currentStatus', 'loading']),
  };
};

export default connect(mapStateToProps)(BusFleetCurrentStatusPanel);
